import React, { useCallback, useEffect, useMemo, useState } from 'react';

import UsersTable from '../components/UsersTable';
import ResetPasswordModal from '../components/ResetPasswordModal';
import CreateUserForm from '../components/CreateUserForm';

import userService from '../services/userService';
import firmService from '../services/firmService';
import '../styles/users.css';

const getErrorMessage = (error) =>
  error.response?.data?.message || error.message || 'Something went wrong.';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [firms, setFirms] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [firmFilter, setFirmFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [resetUser, setResetUser] = useState(null);
  const [creatingUser, setCreatingUser] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [userData, firmData] = await Promise.all([
        userService.getUsers({ role: 'distributor' }),
        firmService.getFirms(),
      ]);

      // Match the response handling used in AdminDashboard.js.
      const usersList = Array.isArray(userData)
        ? userData
        : Array.isArray(userData?.users)
          ? userData.users
          : [];

      const firmsList = Array.isArray(firmData)
        ? firmData
        : Array.isArray(firmData?.firms)
          ? firmData.firms
          : [];

      setUsers(usersList);
      setFirms(firmsList);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();

    return users.filter((user) => {
      const matchesSearch =
        !term ||
        [user.name, user.username, user.id].some((value) =>
          String(value ?? '').toLowerCase().includes(term)
        );

      const matchesFirm =
        firmFilter === 'all' || String(user.firm_id) === firmFilter;

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && user.is_active === true) ||
        (statusFilter === 'inactive' && user.is_active === false);

      return matchesSearch && matchesFirm && matchesStatus;
    });
  }, [users, search, firmFilter, statusFilter]);

  const handleCreateUser = async (payload) => {
    setSaving(true);
    setError('');

    try {
      await userService.createUser(payload);
      setCreatingUser(false);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (user, isActive) => {
    const action = isActive ? 'activate' : 'deactivate';

    if (
      !window.confirm(
        `Are you sure you want to ${action} ${user.username}?`
      )
    ) {
      return;
    }

    setError('');

    try {
      await userService.updateUserStatus(user.id, isActive);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleResetPassword = async (user, password) => {
    setSaving(true);
    setError('');

    try {
      await userService.resetUserPassword(user.id, password);
      setResetUser(null);
    } catch (err) {
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="admin-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-eyebrow">ACCOUNT MANAGEMENT</p>
          <h1>Distributor Users</h1>
          <p>Manage firm accounts, access status and passwords.</p>
        </div>

        <button
          type="button"
          className="admin-button admin-button-primary"
          onClick={() => setCreatingUser((previous) => !previous)}
        >
          {creatingUser ? 'Cancel' : '+ Create User'}
        </button>
      </header>

      {error && (
        <div className="admin-alert admin-alert-error" role="alert">
          {error}
        </div>
      )}

      {creatingUser && (
        <section className="admin-section">
          <CreateUserForm
            firms={firms}
            onSubmit={handleCreateUser}
            onCancel={() => setCreatingUser(false)}
            submitting={saving}
          />
        </section>
      )}

      <section className="admin-filter-bar">
        <div className="admin-search-field">
          <label htmlFor="user-search">Search users</label>
          <input
            id="user-search"
            type="search"
            placeholder="Search by name, username or ID..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="admin-filter-field">
          <label htmlFor="user-firm-filter">Firm</label>
          <select
            id="user-firm-filter"
            value={firmFilter}
            onChange={(event) => setFirmFilter(event.target.value)}
          >
            <option value="all">All firms</option>
            {firms.map((firm) => (
              <option key={firm.id} value={String(firm.id)}>
                {firm.firm_name}
              </option>
            ))}
          </select>
        </div>

        <div className="admin-filter-field">
          <label htmlFor="user-status-filter">Status</label>
          <select
            id="user-status-filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <button
          type="button"
          className="admin-button admin-button-secondary"
          onClick={loadData}
          disabled={loading}
        >
          Refresh
        </button>
      </section>

      <section className="admin-section">
        <div className="admin-section-header">
          <div>
            <h2>All Distributor Accounts</h2>
            <p>
              Showing {filteredUsers.length} of {users.length} users
            </p>
          </div>
        </div>

        <UsersTable
          users={filteredUsers}
          loading={loading}
          onStatusChange={handleStatusChange}
          onResetPassword={setResetUser}
        />
      </section>

      <ResetPasswordModal
        user={resetUser}
        isOpen={Boolean(resetUser)}
        onClose={() => setResetUser(null)}
        onSubmit={handleResetPassword}
        submitting={saving}
      />
    </main>
  );
};

export default UsersPage;